import { useEffect, useState } from 'react';
import {
    Button, Col, Form, Input, notification, PageHeader, Radio, Row,
    Space, Select, Typography,
} from 'antd';

import { useDispatch } from 'react-redux';
import {
    getDangerZones, createDangerZone, deleteDangerZone, updateDangerZone,
} from '../api/danger-zones';
import { IDangerZone, EDangerZoneStatus } from '../types/danger-zone';
import { openModal } from '../actions/modals';
import { ModalTypes } from '../types/modals';
import { getPathsFromPolygonForDangerZone } from '../utils/polygons';
import { DangerZoneMap } from '../components/DangerZoneMap';
import { DangerZoneTable } from '../components/DangerZoneTable';

const { Option } = Select;
const { Text } = Typography;

const ALLOWED_COUNTRIES = [
    { code: 'EC', name: 'Ecuador' },
    { code: 'MX', name: 'México' },
    { code: 'CO', name: 'Colombia' },
];

export const DangerZone = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [dangerZones, setDangerZones] = useState<IDangerZone[]>([]);
    const [newPolygon, setNewPolygon] = useState<google.maps.Polygon>();
    const [selectedCountry, setSelectedCountry] = useState('EC');
    const [loading, setLoading] = useState(false);
    const [editingZone, setEditingZone] = useState<IDangerZone | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    const fetchGetDangerZones = () => {
        setLoading(true);
        getDangerZones({ limit: 100, country: selectedCountry })
            .then((response) => {
                setDangerZones(response.data.data);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchDeleteDangerZone = (id: string) => {
        setDangerZones(dangerZones.filter((zone) => zone._id !== id));
    };

    useEffect(() => {
        fetchGetDangerZones();
    }, [selectedCountry]);

    const onPolygonComplete = (polygon: google.maps.Polygon) => {
        setNewPolygon(polygon);
    };

    const onSubmit = async (values: any) => {
        if (!newPolygon && !isEditing) {
            notification.error({
                message: 'Error',
                description: 'No polygon selected',
            });
            return null;
        }
        try {
            if (isEditing && editingZone) {
                // Actualizar zona existente
                const coordinates = newPolygon ? getPathsFromPolygonForDangerZone(newPolygon) : editingZone.geoLongLat.coordinates;
                const payload = {
                    ...values,
                    coordinates,
                    country: selectedCountry,
                };
                const { data } = await updateDangerZone(editingZone._id, payload);
                setDangerZones(dangerZones.map((zone) => (zone._id === editingZone._id ? data.data : zone)));
                notification.success({
                    message: 'Danger Zone updated',
                    description: 'Danger Zone updated successfully',
                });
                setIsEditing(false);
                setEditingZone(null);
            } else {
                // Crear nueva zona
                const coordinates = getPathsFromPolygonForDangerZone(newPolygon!);
                console.log('Coordinates for danger zone:', coordinates);
                const payload = {
                    ...values,
                    coordinates,
                    country: selectedCountry,
                };
                const { data } = await createDangerZone(payload);
                setDangerZones([...dangerZones, data.data]);
                notification.success({
                    message: 'Danger Zone added',
                    description: 'Danger Zone added successfully',
                });
            }
            newPolygon?.setMap(null);
            setNewPolygon(undefined);
            form.resetFields();
        } catch (error: any) {
            const { message } = error.response.data;
            notification.error({
                message: 'Error',
                description: message,
            });
        } finally {
            newPolygon?.setMap(null);
            setNewPolygon(undefined);
        }
    };

    const handleReset = () => {
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
    };

    const handleCountryChange = (country: string) => {
        setSelectedCountry(country);
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
        // Limpiar modo edición
        setIsEditing(false);
        setEditingZone(null);
    };

    const handleZoneSelect = (zone: IDangerZone) => {
        setEditingZone(zone);
    };

    const handleEditZone = (zone: IDangerZone) => {
        setEditingZone(zone);
        setIsEditing(true);
        form.setFieldsValue({
            status: zone.status,
            notes: zone.notes || '',
        });
        // Limpiar polígono existente para dibujar uno nuevo
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
    };

    const handleDeleteZone = async (zone: IDangerZone) => {
        try {
            await deleteDangerZone(zone._id);
            setDangerZones(dangerZones.filter((dangerZone) => dangerZone._id !== zone._id));
            setEditingZone(null);
            notification.success({
                message: 'Danger Zone deleted successfully',
            });
        } catch (error: any) {
            notification.error({
                message: 'Error deleting danger zone',
                description: error.response?.data?.message || 'An error occurred',
            });
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingZone(null);
        form.resetFields();
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
    };

    return (
        <div>
            <PageHeader
                className="site-page-header"
                title="Danger Zones"
            />

            <Row gutter={[16, 16]}>
                <Col span={6}>
                    <div style={{ padding: '16px' }}>
                        <Form form={form} layout="vertical" onFinish={onSubmit}>
                            <Form.Item label="Country">
                                <Select
                                    value={selectedCountry}
                                    onChange={handleCountryChange}
                                    style={{ width: '100%' }}
                                    disabled={isEditing}
                                >
                                    {ALLOWED_COUNTRIES.map((country) => (
                                        <Option key={country.code} value={country.code}>
                                            {country.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="status"
                                label="Zone Status"
                                rules={[{ required: true, message: 'Please select a status!' }]}
                            >
                                <Select placeholder="Select a status">
                                    <Option value={EDangerZoneStatus.YELLOW}>
                                        <Text style={{ color: '#FFD700' }}>Medium</Text>
                                    </Option>
                                    <Option value={EDangerZoneStatus.ORANGE}>
                                        <Text style={{ color: '#FFA500' }}>High</Text>
                                    </Option>
                                    <Option value={EDangerZoneStatus.RED}>
                                        <Text style={{ color: '#FF0000' }}>Critical</Text>
                                    </Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="notes"
                                label="Notes"
                            >
                                <Input.TextArea rows={4} placeholder="Add notes about this danger zone..." />
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" disabled={!newPolygon && !isEditing}>
                                        {isEditing ? 'Update Danger Zone' : 'Create Danger Zone'}
                                    </Button>
                                    {isEditing && (
                                        <Button onClick={handleCancelEdit}>
                                            Cancel
                                        </Button>
                                    )}
                                    <Button onClick={handleReset}>
                                        Reset
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                </Col>

                <Col span={18}>
                    <DangerZoneMap
                        dangerZones={dangerZones}
                        selectedCountry={selectedCountry}
                        onPolygonComplete={onPolygonComplete}
                        onZoneSelect={handleZoneSelect}
                        onEditZone={handleEditZone}
                        onDeleteZone={handleDeleteZone}
                    />
                </Col>
            </Row>
        </div>
    );
};
